<template>
  <div class="relative" ref="containerRef">
    <button
      @click="open = !open"
      class="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
      :aria-label="open ? 'Close language menu' : 'Change language'"
    >
      <span>{{ currentFlag }}</span>
      <span class="hidden sm:inline uppercase">{{ currentLocale }}</span>
      <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <transition name="fade">
      <div
        v-if="open"
        class="absolute right-0 mt-1 w-28 rounded-lg border border-slate-200 bg-white shadow-lg z-50"
        role="menu"
      >
        <button
          v-for="loc in LOCALES"
          :key="loc.code"
          @click="switchLocale(loc.code)"
          class="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
          :class="loc.code === currentLocale ? 'text-blue-600 bg-blue-50' : ''"
          role="menuitem"
        >
          <span>{{ loc.flag }}</span>
          <span>{{ loc.label }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '@/i18n'

const LOCALES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski',  flag: '🇵🇱' },
]

const router = useRouter()
const route  = useRoute()
const { locale } = useI18n()

const open = ref(false)
const containerRef = ref(null)

const currentLocale = computed(() => locale.value || DEFAULT_LOCALE)
const currentFlag   = computed(() => LOCALES.find(l => l.code === currentLocale.value)?.flag || '🌐')

function switchLocale(targetLocale) {
  open.value = false

  if (targetLocale === currentLocale.value) return

  try {
    localStorage.setItem('pfLocale', targetLocale)
  } catch (_) { /* storage unavailable */ }

  const currentPath = route.fullPath

  let newPath
  if (targetLocale === DEFAULT_LOCALE) {
    newPath = currentPath.replace(/^\/pl(\/|$)/, (_, sep) => sep || '/')
  } else if (SUPPORTED_LOCALES.includes(targetLocale)) {
    const withoutLocale = currentPath.replace(/^\/pl(\/|$)/, (_, sep) => sep || '/')
    newPath = '/' + targetLocale + (withoutLocale === '/' ? '' : withoutLocale)
  } else {
    newPath = currentPath
  }

  router.push(newPath)
}

function onClickOutside(e) {
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    open.value = false
  }
}

onMounted(() => document.addEventListener('click', onClickOutside, true))
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside, true))
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
